import {
  ActiveTariff,
  getTariffLabel,
} from '@getpopsure/private-health-insurance-pricing-engine';
import { englishFormattedEuroCurrency } from '@getpopsure/public-utility';
import {
  BottomOrRegularModal,
  Input,
  TableInfoButton,
} from '@popsure/dirty-swan';
import classNames from 'classnames';
import Markdown from 'components/markdown';
import {
  CarouselOption,
  PlanCarousel,
} from 'components/PlanCarousel/PlanCarousel';
import NextImage from 'next/image';
import { Trans, useTranslation } from 'next-i18next';
import { useState } from 'react';
import AnimateHeight from 'react-animate-height';

import {
  CalculationResultsTextData,
  CalculatorInputData,
  PersonKey,
  PRICE_PLACEHOLDER_TEXT,
} from '../FamilyCalculator/model';
import patternBG from './assets/pattern.svg';
import styles from './Calculator.module.scss';
import { ShortTermQuestions } from './components/ShortTermQuestions/ShortTermQuestions';
import { PriceLine } from './PriceLine';

type CalculatorProps = {
  personKey: PersonKey;
  inputData: CalculatorInputData;
  setInputDataValue: (
    attributeName: keyof CalculatorInputData
  ) => (value: unknown) => void;
  resultsText?: CalculationResultsTextData;
  price?: number;
  employerPaysBackAmount?: number;
  isSimpleMode?: boolean;
  planCarouselOptions: CarouselOption<ActiveTariff>[];
  smallerPriceFont?: boolean;
};

export const Calculator = ({
  personKey,
  inputData,
  setInputDataValue,
  resultsText,
  price,
  employerPaysBackAmount,
  isSimpleMode,
  planCarouselOptions,
  smallerPriceFont,
}: CalculatorProps) => {
  const { t } = useTranslation();

  const [showPrivateLightModal, setShowPrivateLightModal] = useState(false);
  const [showOccupationModal, setShowOccupationModal] = useState(false);
  const [showPriceBreakdownModal, setShowPriceBreakdownModal] = useState(false);

  const {
    birthYear,
    income,
    employmentStatus,
    isPlanningToStayMoreThan5Years,
    hasPermanentResidencyPermit,
    hasEUPassport,
    plan,
  } = inputData ?? {};

  const setPlan = setInputDataValue('plan');
  const setBirthYear = setInputDataValue('birthYear');
  const setIncome = setInputDataValue('income');
  const setEmploymentStatus = setInputDataValue('employmentStatus');
  const setHasEUPassport = setInputDataValue('hasEUPassport');
  const setHasPermanentResidencyPermit = setInputDataValue(
    'hasPermanentResidencyPermit'
  );
  const setIsPlanningToStayMoreThan5Years = setInputDataValue(
    'isPlanningToStayMoreThan5Years'
  );

  const isEmployedInGermany = employmentStatus === 'EMPLOYED_IN_GERMANY';

  const isDependent = personKey !== 'MAIN';

  const priceStr = price
    ? englishFormattedEuroCurrency(Math.round(price), true)
    : PRICE_PLACEHOLDER_TEXT;

  const fullPrice = (price ?? 0) + (employerPaysBackAmount ?? 0);

  const showShortTermNote =
    ['HiMedical', 'HiMedicalPlus'].includes(plan ?? 'NKSelectS') &&
    !isSimpleMode;

  return (
    <section className={`bg-grey-100 ${styles.section}`}>
      <BottomOrRegularModal
        isOpen={showPrivateLightModal}
        title={t(
          'privatehealth:calculator.privateLightModal.title',
          'What is the private short-term plan?'
        )}
        onClose={() => setShowPrivateLightModal(false)}
      >
        <Markdown styling={{ p: 'p-p pt16 px24 pb24 wmx6' }}>
          {t(
            'privatehealth:calculator.privateLightModal.description',
            'Since you’re from outside the EU and have been in Germany for less than 4 years, we offer you our short-term coverage that’s valid up to 5 years after you enter Germany and fulfills all requirements for your visa. This plan has similar benefits to our long-term plans but does not include **old age savings**, which makes it up to 50% cheaper.\n\nThe old age savings reserves are meant to keep private insurance affordable as customers get older, but aren’t necessary for those who plan on leaving Germany.\n\nIf you stay in Germany for longer than 5 years, we will help you transfer to our long-term tariff. The switch is easy and any of your pre-existing conditions will be covered. You will start paying old age savings from that point onward.'
          )}
        </Markdown>
      </BottomOrRegularModal>
      <BottomOrRegularModal
        isOpen={showOccupationModal}
        title={t(
          'privatehealth:calculator.occupationModal.title',
          'Working for an international company?'
        )}
        onClose={() => setShowOccupationModal(false)}
      >
        <p className="p-p p24 pt16 wmx6">
          {t(
            'privatehealth:calculator.occupationModal.description',
            'You are considered employed if your company has an office in Germany or a German tax accountant. Otherwise, you can select self-employed.'
          )}
        </p>
      </BottomOrRegularModal>
      <BottomOrRegularModal
        isOpen={showPriceBreakdownModal}
        title={t(
          'privatehealth:calculator.priceBreakdown.title',
          'How is my price calculated?'
        )}
        onClose={() => setShowPriceBreakdownModal(false)}
      >
        <Markdown styling={{ p: 'p-p pt16 px24 wmx6' }}>
          {t(
            'privatehealth:calculator.priceBreakdown.description',
            'Cost is calculated based on the age at sign-up and does not increase based on your age as you get older. **There are no price increases due to newly developed medical conditions**.\n\nThe only time insurance public and private health prices change is when there’s an overall increase in healthcare costs. Historically, the costs have increased 2% per year.'
          )}
        </Markdown>

        {plan && price && (
          <PriceLine
            label={`${getTariffLabel(plan)} plan`}
            price={fullPrice}
            className="mt32"
          />
        )}

        {!!employerPaysBackAmount && (
          <PriceLine
            label="Employer pays back"
            price={employerPaysBackAmount}
            minus
          />
        )}

        <div className={`bg-grey-200 mt16 mb16 ${styles.divider}`} />

        {!!price && (
          <PriceLine label="You pay" price={price} bold className="mb48" />
        )}
      </BottomOrRegularModal>
      <div className={`p-container ${styles.container}`}>
        <NextImage className={styles.bgPattern} src={patternBG} alt="" />
        <div
          className={`d-flex f-wrap bg-white br8 p32 gap24 ${styles.calculatorCard}`}
        >
          <div className={styles.leftCol}>
            <div className="d-flex f-wrap c-gap8 r-gap24 w100">
              <div className={styles.half}>
                <Input
                  id="BIRTHYEAR"
                  value={birthYear}
                  onChange={(e) =>
                    setBirthYear(Number(e.currentTarget.value) || 0)
                  }
                  placeholder="1980"
                  label={t(
                    'privatehealth:calculator.birthYear.label',
                    'Year of birth'
                  )}
                />
              </div>
              <div className={styles.half}>
                <Input
                  id="YEARLY_INCOME"
                  value={income}
                  label={t(
                    'privatehealth:calculator.yearlyIncome.label',
                    'Yearly income'
                  )}
                  onChange={(e) =>
                    setIncome(Number(e.currentTarget.value) || 0)
                  }
                  placeholder="80 000"
                  prefix="€"
                />
              </div>
            </div>

            <legend className="d-flex p-p tc-grey-600 w100 mt24 mb8 gap8">
              {t('privatehealth:calculator.occupation.label', 'Occupation')}
              <TableInfoButton onClick={() => setShowOccupationModal(true)} />
            </legend>
            <div className="d-flex f-wrap gap8 w100">
              <div className={styles.third}>
                <input
                  name="OCCUPATION"
                  className="p-radio"
                  type="radio"
                  id="EMPLOYED"
                  value="EMPLOYED"
                  checked={isEmployedInGermany}
                  onChange={() => {
                    setEmploymentStatus('EMPLOYED_IN_GERMANY');
                  }}
                />
                <label htmlFor="EMPLOYED" className="p-label p-label--bordered">
                  {t(
                    'privatehealth:calculator.occupation.employed',
                    'Employed'
                  )}
                </label>
              </div>
              <div className={styles.third}>
                <input
                  name="OCCUPATION"
                  className="p-radio"
                  type="radio"
                  id="SELF_EMPLOYED"
                  value="SELF_EMPLOYED"
                  checked={employmentStatus === 'SELF_EMPLOYED'}
                  onChange={() => {
                    setEmploymentStatus('SELF_EMPLOYED');
                  }}
                />
                <label
                  htmlFor="SELF_EMPLOYED"
                  className="p-label p-label--bordered"
                >
                  {t(
                    'privatehealth:calculator.occupation.selfEmployed',
                    'Self-employed'
                  )}
                </label>
              </div>
              <div className={styles.third}>
                <input
                  name="OCCUPATION"
                  className="p-radio"
                  type="radio"
                  id="OTHER"
                  value="OTHER"
                  checked={employmentStatus === 'OTHER'}
                  onChange={() => {
                    setEmploymentStatus('OTHER');
                  }}
                />
                <label htmlFor="OTHER" className="p-label p-label--bordered">
                  {!isDependent
                    ? t(
                        'privatehealth:calculator.occupation.otherOccupation',
                        'Other'
                      )
                    : t(
                        'privatehealth:calculator.occupation.otherOccupation.spouse',
                        'Unemployed'
                      )}
                </label>
              </div>
            </div>
            {!isSimpleMode && (
              <ShortTermQuestions
                isDependent={isDependent}
                hasEUPassport={hasEUPassport}
                hasPermanentResidencyPermit={hasPermanentResidencyPermit}
                isPlanningToStayMoreThan5Years={isPlanningToStayMoreThan5Years}
                setHasEUPassport={setHasEUPassport}
                setHasPermanentResidencyPermit={setHasPermanentResidencyPermit}
                setIsPlanningToStayMoreThan5Years={
                  setIsPlanningToStayMoreThan5Years
                }
              />
            )}
          </div>
          {!resultsText ? (
            <div
              className={`d-flex fd-column ai-center jc-center ${styles.rightCol}`}
            >
              <div className="d-flex gap8 ai-center">
                <h2
                  className={classNames('p-h1 p--serif tc-primary-500', {
                    'p-h1--xl': !smallerPriceFont,
                  })}
                >
                  {priceStr}
                  <span className={styles.perMonthText}> /mo</span>
                </h2>
                {!!price && (
                  <TableInfoButton
                    className={styles.priceBreakdownButton}
                    onClick={() => setShowPriceBreakdownModal(true)}
                  />
                )}
              </div>
              {!!price && (
                <PlanCarousel
                  options={planCarouselOptions}
                  setCurrentOption={setPlan}
                  currentOptionId={plan}
                />
              )}
              <AnimateHeight
                duration={300}
                height={!!price && showShortTermNote ? 'auto' : 0}
                className="w100"
              >
                <div className="p16 bg-grey-100 br8 mt24">
                  <Trans i18nKey="privatehealth:calculator.results.privateLightInfo">
                    <p className="p-p--small tc-grey-900 fw-bold">
                      You’re seeing our private short-term plan
                    </p>
                    <p className="p-p--small tc-grey-500 mt8">
                      Since you’ve been in Germany for less than 4 years, we’re
                      offering you a slightly different{' '}
                      <button
                        className="ds-interactive-component p-a fw-bold"
                        type="button"
                        onClick={() => setShowPrivateLightModal(true)}
                      >
                        plan that excludes old-age savings
                      </button>
                      .
                    </p>
                  </Trans>
                </div>
              </AnimateHeight>
              <AnimateHeight
                duration={300}
                height={price ? 'auto' : 0}
                className="w100"
              >
                <p className="p-p--small tc-grey-500 mt24 ta-center">
                  {isDependent
                    ? t(
                        'privatehealth:calculator.results.eligibilityDependsOnHealth.spouse',
                        'Eligibility depends on their health'
                      )
                    : t(
                        'privatehealth:calculator.results.eligibilityDependsOnHealth',
                        'Eligibility depends on your health'
                      )}
                </p>
              </AnimateHeight>
            </div>
          ) : (
            <div
              className={`d-flex fd-column jc-center p16 ${styles.rightCol}`}
            >
              <h3 className="p-h3 mb8">{resultsText.infoTitle}</h3>
              <Markdown styling={{ p: 'p-p' }} openLinksInNewTab>
                {resultsText.infoDetails || ''}
              </Markdown>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
